import { useState } from 'react';
import { Flame } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/Sheet';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { useHabitStore } from '@/stores/habitStore';
import { useHabitDetail } from '@/features/habits/useHabitDetail';
import { frequencyLabel } from '@/features/habits/frequencyLabel';
import { spellCount } from '@/lib/numberWords';
import { useIsAtLeast } from '@/hooks/useBreakpoint';
import { toDateKey } from '@/lib/dateKey';
import { formatTime } from '@/lib/time';
import type { Habit, HabitCompletion } from '@/types';
import { HabitRing } from './HabitRing';
import { HabitGrid7 } from './HabitGrid7';
import { HabitForm } from './HabitForm';
import type { HabitFormValues } from '@/features/habits/habitSchema';

export function HabitDetailSheet() {
  const isDesktop = useIsAtLeast('md');
  const { selectedHabitId, mode, setMode, close, prefill } = useHabitDetail();
  const habit = useHabitStore((s) =>
    selectedHabitId ? (s.habits.find((h) => h.id === selectedHabitId) ?? null) : null,
  );
  const addHabit = useHabitStore((s) => s.addHabit);
  const updateHabit = useHabitStore((s) => s.updateHabit);
  const removeHabit = useHabitStore((s) => s.removeHabit);
  const logCompletionForDate = useHabitStore((s) => s.logCompletionForDate);
  const completions = useHabitStore((s) => s.completions);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const open = mode === 'add' || habit !== null;
  const side = isDesktop ? 'right' : 'bottom';

  const handleAddSubmit = (values: HabitFormValues) => {
    addHabit({
      name: values.name,
      icon: values.icon,
      color: values.color,
      frequency: values.frequency,
      ...(values.customDays !== undefined ? { customDays: values.customDays } : {}),
      ...(values.scheduledTime !== undefined ? { scheduledTime: values.scheduledTime } : {}),
      ...(values.durationMinutes !== undefined ? { durationMinutes: values.durationMinutes } : {}),
    });
    toast.success('Habit added');
    close();
  };

  const handleEditSubmit = (values: HabitFormValues) => {
    if (!habit) return;
    updateHabit(habit.id, {
      name: values.name,
      icon: values.icon,
      color: values.color,
      frequency: values.frequency,
      ...(values.customDays !== undefined ? { customDays: values.customDays } : {}),
      ...(values.scheduledTime !== undefined ? { scheduledTime: values.scheduledTime } : {}),
      ...(values.durationMinutes !== undefined ? { durationMinutes: values.durationMinutes } : {}),
    });
    toast.success('Habit updated');
    setMode('view');
  };

  const handleDelete = (h: Habit) => {
    const completionsSnapshot: HabitCompletion[] = completions.filter((c) => c.habitId === h.id);
    removeHabit(h.id);
    setConfirmDeleteOpen(false);
    close();
    toast(`Habit deleted — ${h.name}`, {
      duration: 4000,
      action: {
        label: 'Undo',
        onClick: () => {
          const restored = addHabit({
            name: h.name,
            icon: h.icon,
            color: h.color,
            frequency: h.frequency,
            ...(h.customDays ? { customDays: h.customDays } : {}),
            ...(h.scheduledTime ? { scheduledTime: h.scheduledTime } : {}),
            ...(h.durationMinutes !== undefined ? { durationMinutes: h.durationMinutes } : {}),
            ...(h.description ? { description: h.description } : {}),
          });
          for (const c of completionsSnapshot) {
            logCompletionForDate(restored.id, c.date);
          }
        },
      },
    });
  };

  return (
    <Sheet open={open} onOpenChange={(o) => (!o ? close() : undefined)}>
      <SheetContent side={side}>
        <SheetTitle className="text-title-lg font-semibold text-t1">
          {mode === 'add' ? 'New habit' : mode === 'edit' ? 'Edit habit' : (habit?.name ?? '')}
        </SheetTitle>

        {mode === 'add' && (
          <div className="mt-4">
            <HabitForm
              {...(prefill ? { initialValues: prefill } : {})}
              onSubmit={handleAddSubmit}
              onCancel={close}
              submitLabel="Add"
            />
          </div>
        )}

        {mode === 'edit' && habit && (
          <div className="mt-4">
            <HabitForm
              initialValues={{
                name: habit.name,
                icon: habit.icon,
                color: habit.color,
                frequency: habit.frequency,
                customDays: habit.customDays,
                scheduledTime: habit.scheduledTime,
                durationMinutes: habit.durationMinutes,
              }}
              onSubmit={handleEditSubmit}
              onCancel={() => setMode('view')}
              submitLabel="Save"
            />
          </div>
        )}

        {mode === 'view' && habit && (
          <div className="mt-4 flex flex-col gap-5">
            <header className="flex items-center gap-3">
              <HabitRing
                state={
                  completions.some(
                    (c) => c.habitId === habit.id && c.date === toDateKey(new Date()),
                  )
                    ? 'done'
                    : 'todo'
                }
                icon={habit.icon}
                size={44}
                color={habit.color}
              />
              <div>
                <p className="flex items-center gap-1.5 text-sm text-t2">
                  {habit.currentStreak >= 3 && <Flame className="h-3 w-3 shrink-0" aria-hidden />}
                  <span>
                    <span className="font-mono tabular-nums">{habit.currentStreak}</span>-day streak
                    · best <span className="font-mono tabular-nums">{habit.longestStreak}</span>{' '}
                    days
                  </span>
                </p>
                <p className="text-xs text-t3">
                  {frequencyLabel(habit.frequency)}
                  {habit.scheduledTime ? ` · ${formatTime(habit.scheduledTime)}` : ''}
                  {habit.durationMinutes ? ` · ${habit.durationMinutes}m` : ''}
                </p>
              </div>
            </header>

            <section>
              <h3 className="text-sm font-medium text-t1 mb-2">Last 7 days</h3>
              <HabitGrid7 habitId={habit.id} />
            </section>

            <div className="flex items-center justify-between pt-2">
              <Button type="button" variant="primary" onClick={() => setMode('edit')}>
                Edit
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setConfirmDeleteOpen(true)}
                className="text-crit can-hover:hover:bg-crit-bg can-hover:hover:text-t1"
              >
                Delete
              </Button>
            </div>
          </div>
        )}

        {habit && (
          <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
            <DialogContent>
              <DialogTitle>Delete {habit.name}?</DialogTitle>
              <DialogDescription>
                {(() => {
                  const n = completions.filter((c) => c.habitId === habit.id).length;
                  if (n === 0) return 'This removes the habit. It has no logged completions yet.';
                  return `This removes the habit and its ${spellCount(n)} ${n === 1 ? 'completion' : 'completions'}.`;
                })()}
              </DialogDescription>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setConfirmDeleteOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  className="bg-crit text-canvas can-hover:hover:bg-crit can-hover:hover:brightness-95 active:bg-crit active:brightness-95"
                  onClick={() => handleDelete(habit)}
                >
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </SheetContent>
    </Sheet>
  );
}
